import { urls } from '@game/constants';
import type { AreaSoundTrack } from '@game/game-data';
import { isMusicTab } from '@lib/musicTabCoordinator';
import { Howl } from 'howler';

import { Logger } from '@util/logger';
import type { MusicContext, MusicTrack } from '@store/musicStore';
import { CanSkipTracks, musicStore } from '@store/musicStore';
import { persistentStore } from '@store/persistentStore';

let loopDelayTimeoutId: ReturnType<typeof setTimeout> | null = null;
let pendingNextTrackIndex: number | null = null;

// Cancels the scheduled timeout and clears the countdown UI, but keeps pendingNextTrackIndex.
function cancelPendingTimeout(): void {
  if (loopDelayTimeoutId !== null) {
    clearTimeout(loopDelayTimeoutId);
    loopDelayTimeoutId = null;
    musicStore.getState().setLoopDelayEndsAt(null);
  }
}

// Full cleanup: cancels the timeout and clears all pending state.
function clearLoopDelayTimeout(): void {
  cancelPendingTimeout();
  pendingNextTrackIndex = null;
}

function scheduleNextTrack(nextIndex: number, delayMs: number): void {
  pendingNextTrackIndex = nextIndex;
  if (delayMs > 0) {
    musicStore.getState().setLoopDelayEndsAt(Date.now() + delayMs);
  }
  loopDelayTimeoutId = setTimeout(() => {
    loopDelayTimeoutId = null;
    pendingNextTrackIndex = null;
    musicStore.getState().setLoopDelayEndsAt(null);
    // Only start playback if this tab is still the active music tab.
    if (!isMusicTab()) {
      return;
    }
    playTrackAtIndex(nextIndex);
  }, delayMs);
}

/**
 * Parses an AreaSoundTrack into a MusicTrack.
 * Groups different file formats of the same track together.
 */
function parseSoundtrackToMusicTrack(soundtrack: AreaSoundTrack): MusicTrack {
  const tracks = soundtrack.urls.includes(',') ? soundtrack.urls.split(',') : [soundtrack.urls];
  const processedTracks = tracks.map((track) => track.trim()).filter((track) => track.length > 0);

  // Convert all tracks to full URLs
  const trackUrls = processedTracks.map((track) => (track.startsWith('http') ? track : `${urls.cdnAudio}${track}`));

  return {
    urls: trackUrls,
    title: soundtrack.title,
  };
}

/**
 * Converts an array of AreaSoundTracks to an array of MusicTracks.
 */
function parseAreaSoundtracksToPlaylist(soundtracks: AreaSoundTrack[]): MusicTrack[] {
  if (!soundtracks || soundtracks.length === 0) {
    return [];
  }

  return soundtracks.map((soundtrack) => parseSoundtrackToMusicTrack(soundtrack));
}

/**
 * Plays music for a specific context with the given playlist.
 * Handles cleanup of previous music and smooth transitions.
 * Always updates the music store state, but only creates Howl instances if this tab is the music tab.
 */
function playMusic(context: MusicContext, playlist: MusicTrack[], canSkipTracks = CanSkipTracks.Yes): void {
  const currentHowl = musicStore.getState().currentHowl;
  const currentContext = musicStore.getState().context;
  const currentPlaylist = musicStore.getState().playlist;

  // Always update the store state so non-music tabs know the current playlist
  const shouldUpdateStore = currentContext !== context || playlist !== currentPlaylist;

  if (shouldUpdateStore) {
    if (playlist.length === 0) {
      musicStore.getState().setContext('none');
      musicStore.getState().setPlaylist([]);
    } else {
      musicStore.getState().setContext(context);
      musicStore.getState().setPlaylist(playlist);
      musicStore.getState().setCanSkipTracks(canSkipTracks);
    }
  }

  // Only create/play Howl instances if this is the music tab
  if (!isMusicTab()) {
    return;
  }

  // If same context, same playlist, and music is already playing, do nothing
  if (currentContext === context && currentPlaylist === playlist && currentHowl?.playing()) {
    return;
  }

  // Helper function to start new music after cleanup
  function startNewMusic(): void {
    if (playlist.length === 0) {
      musicStore.getState().resetMusic();
      return;
    }

    // Set current track index
    musicStore.getState().setCurrentTrackIndex(0);

    // Create and play new music
    playTrackAtIndex(0);
  }

  // Clean up current music with fade out
  if (currentHowl) {
    const volume = currentHowl.volume();
    currentHowl.fade(volume, 0, 500);
    currentHowl.once('fade', () => {
      currentHowl.stop();
      currentHowl.unload();
      // Start new music only after fade completes
      startNewMusic();
    });
  } else {
    // No current music, start immediately
    startNewMusic();
  }
}

/**
 * Cleans up the current Howl instance without triggering callbacks.
 * Clears any pending loop-delay timeout but preserves context/playlist in the store,
 * so restartCurrentMusic() can resume playback later.
 */
export function cleanupCurrentHowl(): void {
  clearLoopDelayTimeout();
  const currentHowl = musicStore.getState().currentHowl;
  if (currentHowl) {
    // Remove all event listeners to prevent callbacks from firing
    currentHowl.off();
    currentHowl.stop();
    currentHowl.unload();
  }
}

/**
 * Plays a specific track from the current playlist.
 *
 * The optional attemptedIndices set is used during error handling to avoid
 * infinitely cycling through a playlist where all tracks fail to load.
 */
function playTrackAtIndex(index: number, attemptedIndices: Set<number> = new Set()): void {
  const { playlist } = musicStore.getState();

  if (index < 0 || index >= playlist.length) {
    return;
  }

  const track = playlist[index];
  if (!track) {
    return;
  }

  // Clean up any existing Howl instance first
  cleanupCurrentHowl();

  // Set the track index before creating the Howl instance
  musicStore.getState().setCurrentTrackIndex(index);

  const volume = persistentStore.getState().volume / 100;

  const howl = new Howl({
    src: track.urls,
    autoplay: true,
    loop: false,
    volume: 0,
    onplay: (): void => {
      musicStore.getState().setIsPlaying(true);
      // Fade in
      howl.fade(0, volume, 500);
    },
    onpause: (): void => {
      musicStore.getState().setIsPlaying(false);
    },
    onstop: (): void => {
      musicStore.getState().setIsPlaying(false);
    },
    onend: (): void => {
      const delayMs = persistentStore.getState().musicLoopDelay;
      const nextIndex = (index + 1) % playlist.length;
      const isWrapAround = nextIndex === 0;
      if (delayMs < 0) {
        pendingNextTrackIndex = nextIndex; // Remember in case the setting changes
        return;
      }
      if (!isWrapAround) {
        scheduleNextTrack(nextIndex, 0);
        return;
      }
      scheduleNextTrack(nextIndex, delayMs);
    },
    onloaderror: (_id, error): void => {
      Logger.error('Failed to load music track:', track.urls, error);
      // Try next track if available, but avoid infinite loops by tracking attempts
      if (playlist.length > 1) {
        const updatedAttempts = new Set([...attemptedIndices, index]);

        if (updatedAttempts.size >= playlist.length) {
          Logger.error('All tracks in playlist failed to load. Stopping music playback.');
          musicStore.getState().resetMusic();
          return;
        }

        const nextIndex = (index + 1) % playlist.length;
        playTrackAtIndex(nextIndex, updatedAttempts);
      }
    },
  });

  musicStore.getState().setCurrentHowl(howl);
}

/**
 * Plays the naming screen music.
 */
export function playNamingMusic(): void {
  const playlist: MusicTrack[] = [
    {
      urls: [`${urls.cdnAudio}naming.webm`, `${urls.cdnAudio}naming.mp3`],
      title: 'Waking Up',
    },
  ];

  playMusic('naming', playlist, CanSkipTracks.No); // No track skipping on naming screen
}

/**
 * Plays area music based on the soundtracks array.
 */
export function playAreaMusic(soundtracks: AreaSoundTrack[]): void {
  const playlist = parseAreaSoundtracksToPlaylist(soundtracks);
  playMusic('area', playlist, CanSkipTracks.Yes); // Can skip tracks in areas
}

/**
 * Stops all music and resets the music state.
 * Returns a promise that resolves when the music has finished fading out.
 */
export function stopMusic(): Promise<void> {
  clearLoopDelayTimeout();
  return new Promise((resolve) => {
    const currentHowl = musicStore.getState().currentHowl;

    if (currentHowl) {
      const volume = currentHowl.volume();
      currentHowl.fade(volume, 0, 300);
      currentHowl.once('fade', () => {
        currentHowl.stop();
        currentHowl.unload();
        musicStore.getState().resetMusic();
        resolve();
      });
    } else {
      musicStore.getState().resetMusic();
      resolve();
    }
  });
}

/**
 * Skips to the next track in the playlist.
 */
export function nextTrack(): void {
  const { playlist, currentTrackIndex, canSkipTracks } = musicStore.getState();

  if (canSkipTracks === CanSkipTracks.No || playlist.length <= 1) {
    return;
  }

  const nextIndex = (currentTrackIndex + 1) % playlist.length;
  playTrackAtIndex(nextIndex);
}

/**
 * Skips to the previous track in the playlist.
 */
export function previousTrack(): void {
  const { playlist, currentTrackIndex, canSkipTracks } = musicStore.getState();

  if (canSkipTracks === CanSkipTracks.No || playlist.length <= 1) {
    return;
  }

  const prevIndex = currentTrackIndex === 0 ? playlist.length - 1 : currentTrackIndex - 1;
  playTrackAtIndex(prevIndex);
}

/**
 * Toggles play/pause of the current music.
 */
export function togglePlayPause(): void {
  const currentHowl = musicStore.getState().currentHowl;

  if (!currentHowl) {
    return;
  }

  if (currentHowl.playing()) {
    currentHowl.pause();
  } else {
    clearLoopDelayTimeout(); // Cancel any pending loop delay when user manually plays
    currentHowl.play();
  }
}

/**
 * Mutes the current music.
 */
export function muteMusic(): void {
  const currentHowl = musicStore.getState().currentHowl;
  if (currentHowl) {
    currentHowl.mute(true);
  }
}

/**
 * Unmutes the current music.
 */
export function unmuteMusic(): void {
  const currentHowl = musicStore.getState().currentHowl;
  if (currentHowl) {
    currentHowl.mute(false);
  }
}

/**
 * Updates the music loop delay setting and, if a countdown is currently active,
 * adjusts the remaining time by the delta between the old and new delays.
 */
export function updateMusicLoopDelay(newDelayMs: number): void {
  const currentDelayMs = persistentStore.getState().musicLoopDelay;
  persistentStore.getState().setMusicLoopDelay(newDelayMs);

  const { context, playlist } = musicStore.getState();
  if (context === 'none' || playlist.length === 0) {
    return; // Music has been stopped; don't reschedule playback
  }

  if (pendingNextTrackIndex === null) {
    return; // Track hasn't ended yet, nothing to adjust
  }

  const nextIndex = pendingNextTrackIndex;

  if (newDelayMs < 0) {
    // Switching to Never — cancel any active countdown but keep pendingNextTrackIndex
    // so switching away from Never later can still start a countdown.
    cancelPendingTimeout();
    return;
  }

  const loopDelayEndsAt = musicStore.getState().loopDelayEndsAt;

  if (loopDelayEndsAt === null) {
    // Was in Never mode (no active countdown) — defensively cancel any stray timeout
    // (e.g. from a 0ms delay) and start a fresh countdown from now.
    cancelPendingTimeout();
    scheduleNextTrack(nextIndex, newDelayMs);
    return;
  }

  // Active countdown exists — shift the end time by the delta.
  const newEndsAt = loopDelayEndsAt - currentDelayMs + newDelayMs;
  const remainingMs = newEndsAt - Date.now();

  cancelPendingTimeout();

  if (remainingMs <= 0) {
    pendingNextTrackIndex = null;
    playTrackAtIndex(nextIndex);
  } else {
    scheduleNextTrack(nextIndex, remainingMs);
  }
}

/**
 * Updates the volume of the current music.
 */
export function setMusicVolume(volume: number): void {
  const currentHowl = musicStore.getState().currentHowl;
  if (currentHowl) {
    currentHowl.volume(volume / 100);
  }
}

/**
 * Restarts the current music context and playlist.
 * Used when a tab claims music tab role to immediately start playback.
 *
 * This function will:
 * - Do nothing if context is 'none' or playlist is empty
 * - Call playMusic with the current context, playlist, and canSkipTracks
 * - Only play if this tab is the music tab (checked by playMusic)
 *
 * @example
 * // After claiming music tab, restart playback
 * if (claimMusicTab(true)) {
 *   restartCurrentMusic();
 * }
 */
export function restartCurrentMusic(): void {
  const { context, playlist, canSkipTracks } = musicStore.getState();

  // Only restart if we have a valid context and playlist
  if (context !== 'none' && playlist.length > 0) {
    playMusic(context, playlist, canSkipTracks);
  }
}
